import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { DNABox, DNADivider, DNAButton, Iffy } from '@alucio/lux-ui';
import { useHistory } from 'react-router';
import { DataProvider, LayoutProvider, RecyclerListView } from 'recyclerlistview';
import { useAllDocuments } from 'src/state/redux/selector/document';
import { useAllCombinedFolders } from 'src/state/redux/selector/folder';
import documentQuery from 'src/state/redux/document/query';
import folderQuery from 'src/state/redux/folder/query';
import DNAEmpty, { EmptyVariant } from 'src/components/DNA/Empty/DNAEmpty';
import { useDNADocumentActions } from 'src/components/DNA/Document/DNADocument.actions';
import DNAActionBar from 'src/components/DNA/ActionBar/DNAActionBar';
import { DocumentORM, FolderORM } from 'src/types/orms';
import DNADocumentDashboardRow from 'src/components/DNA/FlatList/Rows/dashboard/DNADocumentDashboardRow';
import DNAFolderDashboardRow from 'src/components/DNA/FlatList/Rows/dashboard/DNAFolderDashboardRow';
import { useAppSettings } from 'src/state/context/AppSettings';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { useFetchSharedFolders } from 'src/state/redux/slice/sharedFolder';
import WidgetContainer from '../WidgetContainer';
import { ROUTES } from 'src/router/routes';
import { StyleSheet } from 'react-native';

enum FavoriteTab {
  FILES = 'Files',
  FOLDERS = 'Folders',
}

const styles = StyleSheet.create({
  container: {
    height: 326,
    overflow: 'hidden',
  },
})

const FAVORITE_FILES_QUERY = documentQuery.merge(
  documentQuery.filters.published,
  documentQuery.filters.bookmarked,
  documentQuery.sorts.titleAsc,
);

const FAVORITE_FOLDERS_QUERY = folderQuery.merge(
  folderQuery.filters.pinned,
  folderQuery.filters.rootFolder,
  folderQuery.filters.active,
  folderQuery.sorts.updatedAtDesc,
);

const DOCUMENT_ROW_HEIGHT = 88;
const FOLDER_ROW_HEIGHT = 48;

const Favorites: React.FC = () => {
  const history = useHistory();
  const documentActions = useDNADocumentActions();
  const { isOnline } = useAppSettings();
  const [activeTab, setActiveTab] = useState<FavoriteTab>(FavoriteTab.FILES);

  const {
    enableHomeFavoriteFiles,
    enableHomeFavoriteFolders,
    enableSharedFolders,
  } = useFeatureFlags(
    'enableHomeFavoriteFiles',
    'enableHomeFavoriteFolders',
    'enableSharedFolders',
  );

  const { fetchSharedFolders } = useFetchSharedFolders();

  useEffect(() => {
    if (enableSharedFolders && isOnline) {
      fetchSharedFolders();
    }
  }, [isOnline, enableSharedFolders]);

  const showTabs = enableHomeFavoriteFiles && enableHomeFavoriteFolders;

  useEffect(() => {
    if (!enableHomeFavoriteFiles && enableHomeFavoriteFolders) {
      setActiveTab(FavoriteTab.FOLDERS);
    } else if (enableHomeFavoriteFiles && !enableHomeFavoriteFolders) {
      setActiveTab(FavoriteTab.FILES);
    }
  }, [enableHomeFavoriteFiles, enableHomeFavoriteFolders]);

  // FETCH FAVORITE FILES
  const favoriteFiles = useAllDocuments(FAVORITE_FILES_QUERY);

  // FETCH FAVORITE FOLDERS
  const favoriteFolders = useAllCombinedFolders(FAVORITE_FOLDERS_QUERY);

  const handlePressDocument = useCallback(
    (documentORM: DocumentORM) => documentActions.preview(documentORM)(),
    [documentActions],
  );

  const handlePressFolder = useCallback(
    (folderORM: FolderORM) => {
      history.push(`${ROUTES.FOLDERS.PATH}/${folderORM.model.id}`);
    },
    [history],
  );

  const navigateToViewAll = () => {
    if (activeTab === FavoriteTab.FILES) {
      history.push(ROUTES.UI2_FAVORITE_FILES.PATH);
    } else {
      history.push(ROUTES.UI2_FAVORITE_FOLDERS.PATH);
    }
  };

  const dataProvider = useMemo(
    () => {
      const data = activeTab === FavoriteTab.FILES ? favoriteFiles : favoriteFolders;
      return new DataProvider((r1, r2) => r1 !== r2).cloneWithRows(data);
    },
    [activeTab, favoriteFiles, favoriteFolders],
  );

  const layoutProvider = useMemo(
    () => new LayoutProvider(
      () => 'DEFAULT',
      (_type, dim) => {
        dim.width = 1000; // RecyclerListView will handle the actual width
        dim.height = activeTab === FavoriteTab.FILES ? DOCUMENT_ROW_HEIGHT : FOLDER_ROW_HEIGHT;
      },
    ),
    [activeTab],
  );

  const rowRenderer = useCallback(
    (_type: string | number, item: DocumentORM | FolderORM, index: number) => {
      if (activeTab === FavoriteTab.FILES) {
        return (
          <DNABox appearance="col">
            <Iffy is={index > 0}>
              <DNADivider variant="secondary" />
            </Iffy>
            <DNADocumentDashboardRow
              isOnline={isOnline}
              ORM={item as DocumentORM}
              onPress={handlePressDocument}
              variant="dashboardFavorites"
            />
          </DNABox>
        );
      }
      return (
        <DNABox appearance="col">
          <Iffy is={index > 0}>
            <DNADivider variant="secondary" />
          </Iffy>
          <DNAFolderDashboardRow
            isOnline={isOnline}
            ORM={item as FolderORM}
            onPress={handlePressFolder}
            variant="dashboardFavorites"
          />
        </DNABox>
      );
    },
    [activeTab, handlePressDocument, handlePressFolder, isOnline],
  );

  const hasItems = activeTab === FavoriteTab.FILES
    ? favoriteFiles.length > 0
    : favoriteFolders.length > 0;

  const emptyVariant = activeTab === FavoriteTab.FILES
    ? EmptyVariant.DocumentFavoriteDashboardEmpty
    : EmptyVariant.FolderFavoriteDashboardEmpty;

  return (
    <WidgetContainer title="Favorites">
      <DNABox appearance="col" fill>
        {/* TABS */}
        <Iffy is={showTabs}>
          <DNAActionBar
            spacing="sm"
            style={{ marginBottom: 8 }}
            itemDefaults={{ colorMode: 'light' }}
            childFill={true}
          >
            <DNAActionBar.Item
              appearance={activeTab === FavoriteTab.FILES ? 'filled' : 'ghost'}
              status={activeTab === FavoriteTab.FILES ? 'primary' : 'subtle'}
              size="sm"
              stretch
              style={{ height: 32 }}
              onPress={() => setActiveTab(FavoriteTab.FILES)}
              key={FavoriteTab.FILES}
            >
              {FavoriteTab.FILES}
            </DNAActionBar.Item>
            <DNAActionBar.Item
              appearance={activeTab === FavoriteTab.FOLDERS ? 'filled' : 'ghost'}
              status={activeTab === FavoriteTab.FOLDERS ? 'primary' : 'subtle'}
              stretch
              size="sm"
              style={{ height: 32 }}
              onPress={() => setActiveTab(FavoriteTab.FOLDERS)}
              key={FavoriteTab.FOLDERS}
            >
              {FavoriteTab.FOLDERS}
            </DNAActionBar.Item>
          </DNAActionBar>
        </Iffy>

        {/* CONTENT */}
        <DNABox
          style={styles.container}
        >
          <Iffy is={hasItems}>
            <RecyclerListView
              layoutProvider={layoutProvider}
              dataProvider={dataProvider}
              rowRenderer={rowRenderer}
              forceNonDeterministicRendering={false}
            />
          </Iffy>
          <Iffy is={!hasItems}>
            <DNABox fill alignX="center" alignY="center">
              <DNAEmpty emptyVariant={emptyVariant} />
            </DNABox>
          </Iffy>
        </DNABox>

        <Iffy is={hasItems}>
          <DNABox appearance="col" spacing="md">
            <DNADivider />
            <DNAButton
              appearance="ghost"
              size="sm"
              onPress={navigateToViewAll}
            >
              View all
            </DNAButton>
          </DNABox>
        </Iffy>
      </DNABox>
    </WidgetContainer>
  );
};

export default Favorites;
